import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronLeft, ChevronRight, Calendar, Sparkles, Dumbbell, Heart, Leaf } from 'lucide-react';
import { motion } from 'framer-motion';
import metalogo from '../../assets/metalogo.webp';
import heroWellnessLake from '../../assets/hero_wellness_lake.jpg';
import heroClassesBg from '../../assets/hero_classes_bg.jpg';
import './homepage.css';
import PillarsSection from './PillarsSection';
import TrainersSection from './TrainersSection';
import TreatmentsSection from './TreatmentsSection';
import ReviewsSection from './ReviewsSection';
import { mainAdminApi } from '../../config';
import SEO from '../SEO';

interface SubCategory {
  name: string;
  isActive: boolean;
}

interface PillarCategory {
  _id: string;
  name: string;
  description?: string;
  icon?: string;
  subCategories?: SubCategory[];
  isActive: boolean;
}

const Homepage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [pillars, setPillars] = useState<PillarCategory[]>([]);
  const [loadingPillars, setLoadingPillars] = useState(true);
  const [currentHeroSlide, setCurrentHeroSlide] = useState(0);
  const [isHeroHovered, setIsHeroHovered] = useState(false);

  const nextHeroSlide = () => {
    setCurrentHeroSlide((prev) => (prev + 1) % 2);
  };

  const prevHeroSlide = () => {
    setCurrentHeroSlide((prev) => (prev - 1 + 2) % 2);
  };

  useEffect(() => {
    if (isHeroHovered) return;
    const timer = setInterval(() => {
      setCurrentHeroSlide((prev) => (prev + 1) % 2);
    }, 6000);
    return () => clearInterval(timer);
  }, [isHeroHovered]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);

    const fetchPillars = async () => {
      try {
        const response = await mainAdminApi.get<any>('/pillar-categories');
        if (response.data && response.data.categories) {
          setPillars(response.data.categories.filter((p: PillarCategory) => p.isActive));
        }
      } catch (error) {
        console.error('Error fetching footer pillars:', error);
      } finally {
        setLoadingPillars(false);
      }
    };
    fetchPillars();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);



  return (
    <>
      <SEO 
        title="MetaFit Wellness – Your Home for Holistic Wellness"
        description="Book yoga, therapy, healers, nutrition experts and wellness sessions online. Discover holistic wellness practitioners, live sessions, retreats and holistic care with MetaFit Wellness."
        keywords="Metafit, MetaFit Wellness, wellness, fitness, live classes, yoga, online consultations, India, healers, nutrition, therapy"
        url="https://www.metafitwellness.com/"
        canonical="https://www.metafitwellness.com/"
        image="https://www.metafitwellness.com/metafit.webp"
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "MetaFit Wellness",
            "url": "https://www.metafitwellness.com/",
            "logo": "https://www.metafitwellness.com/metalogo.webp",
            "description": "Book yoga, therapy, healers, nutrition experts and wellness sessions online with MetaFit Wellness.",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "First Floor, Above Arbuda Electric, Behind Celebration Mall, Plot No. 5, Garh Magri, Ratneshwar Colony, Pulla Bhuwana",
              "addressLocality": "Udaipur",
              "addressRegion": "Rajasthan",
              "postalCode": "313001",
              "addressCountry": "IN"
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "telephone": "+918829912389",
              "contactType": "customer service",
              "email": "cto.metafit@gmail.com",
              "areaServed": "IN",
              "availableLanguage": ["en", "Hindi"]
            },
            "sameAs": [
              "https://www.instagram.com/metafitwellness/",
              "https://www.linkedin.com/company/meta-fit-wellness",
              "https://www.facebook.com/61565468193885"
            ]
          },
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            "name": "MetaFit Wellness",
            "url": "https://www.metafitwellness.com/",
            "potentialAction": {
              "@type": "SearchAction",
              "target": "https://www.metafitwellness.com/services?q={search_term_string}",
              "query-input": "required name=search_term_string"
            }
          }
        ]}
      />      {/* HERO SLIDER */}
      <div className="hero-outer-bg">
        <div className="hero-banner-wrap">
          <div 
            className="hero-slider-container"
            onMouseEnter={() => setIsHeroHovered(true)}
            onMouseLeave={() => setIsHeroHovered(false)}
          >
            {/* Slides Track */}
            <div 
              className="hero-slides-track"
              style={{
                transform: `translateX(-${currentHeroSlide * 100}%)`,
              }}
            >
              {/* SLIDE 1 */}
              <div className="hero-slide-item">
                <section className="hero hero-slide-1" style={{ backgroundImage: `url(${heroWellnessLake})` }}>
                  <div className="wrap hero-grid">
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8 }}
                    >
                      <h1>
                        Book a real practitioner<br />
                        for your next <span className="accent">breath, stretch<br />or session.</span>
                      </h1>
                      <p className="lead">
                        MetaFit connects you with certified instructors and consultants across India — for live classes or one-on-one consultations, online or in person.
                      </p>
                      
                      <div className="hero-actions">
                        <button 
                          onClick={() => document.getElementById('trainers')?.scrollIntoView({ behavior: 'smooth' })}
                          className="btn-hero-explore"
                        >
                          Explore Professionals <ChevronDown size={18} />
                        </button>
                      </div>

                      <div className="trust-row">
                        <div className="trust-item">
                          <div className="num">1,200+</div>
                          <div className="lbl">VERIFIED PRACTITIONERS</div>
                        </div>
                        <div className="trust-divider" />
                        <div className="trust-item">
                          <div className="num">28</div>
                          <div className="lbl">SPECIALITIES</div>
                        </div>
                        <div className="trust-divider" />
                        <div className="trust-item">
                          <div className="num">4.8 / 5</div>
                          <div className="lbl">AVG. SESSION RATING</div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </section>
              </div>

              {/* SLIDE 2 - BOOK YOUR CLASSES */}
              <div className="hero-slide-item">
                <section className="hero hero-slide-classes" style={{ backgroundImage: `url(${heroClassesBg})` }}>
                  <div className="wrap hero-grid">
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8 }}
                    >
                      <div className="hero-pill-badge">
                        <Calendar size={13} className="badge-icon" />
                        <span>Live & Online Classes</span>
                      </div>

                      <h1>
                        Book Your <span className="accent">Classes</span>
                      </h1>
                      <p className="lead">
                        Join expert-led yoga, fitness, meditation and wellness classes, online or in person.
                      </p>
                      
                      {/* 4 Feature Pills */}
                      <div className="hero-classes-pills">
                        <Link to="/classes?category=Yoga" className="hero-class-pill">
                          <Sparkles size={13} className="pill-icon" />
                          <span>Yoga</span>
                        </Link>
                        <Link to="/classes?category=Fitness" className="hero-class-pill">
                          <Dumbbell size={13} className="pill-icon" />
                          <span>Fitness</span>
                        </Link>
                        <Link to="/classes?category=Meditation" className="hero-class-pill">
                          <Heart size={13} className="pill-icon" />
                          <span>Meditation</span>
                        </Link>
                        <Link to="/classes?category=Wellness" className="hero-class-pill">
                          <Leaf size={13} className="pill-icon" />
                          <span>Wellness</span>
                        </Link>
                      </div>

                      <div className="hero-actions">
                        <Link 
                          to="/classes"
                          className="btn-hero-explore btn-hero-classes"
                        >
                          Book Your Classes &rarr;
                        </Link>
                      </div>
                    </motion.div>
                  </div>
                </section>
              </div>
            </div>

            {/* Left Nav Arrow Button */}
            <button 
              type="button"
              className="hero-slider-arrow hero-slider-prev"
              onClick={prevHeroSlide}
              aria-label="Previous Slide"
            >
              <ChevronLeft size={18} strokeWidth={2.4} />
            </button>

            {/* Right Nav Arrow Button */}
            <button 
              type="button"
              className="hero-slider-arrow hero-slider-next"
              onClick={nextHeroSlide}
              aria-label="Next Slide"
            >
              <ChevronRight size={18} strokeWidth={2.4} />
            </button>

            {/* Bottom Dots Indicator Pill */}
            <div className="hero-slider-dots">
              {[0, 1].map((idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setCurrentHeroSlide(idx)}
                  className={`hero-slider-dot ${currentHeroSlide === idx ? 'active' : ''}`}
                  aria-label={`Go to slide ${idx + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* TREATMENTS & RETREATS */}
      <TreatmentsSection />

      {/* TRAINERS */}
      <TrainersSection />

      {/* PILLARS */}
      <PillarsSection pillars={pillars} loading={loadingPillars} />

      {/* REVIEWS & TESTIMONIALS */}
      <ReviewsSection />

      {/* INSTRUCTOR CTA */}
      <section style={{ backgroundColor: "#FFFFFF", paddingTop: "32px", paddingBottom: "56px" }}>
        <div className="wrap">
          <div className="cta-banner relative overflow-hidden">
            {/* Ambient Background Swoosh Waves */}
            <svg
              className="pointer-events-none absolute inset-0 h-full w-full opacity-45 select-none"
              viewBox="0 0 1200 360"
              fill="none"
              preserveAspectRatio="none"
            >
              <path
                d="M-40,360 C260,330 520,240 780,140 C980,60 1100,30 1220,10"
                stroke="url(#waveGrad1)"
                strokeWidth="50"
                strokeLinecap="round"
              />
              <path
                d="M40,380 C360,330 620,220 880,110 C1060,30 1160,10 1240,-20"
                stroke="url(#waveGrad2)"
                strokeWidth="100"
                strokeLinecap="round"
              />
              <path
                d="M-80,290 C220,280 460,190 700,120 C900,60 1060,30 1200,0"
                stroke="url(#waveGrad1)"
                strokeWidth="18"
                strokeLinecap="round"
              />
              <defs>
                <linearGradient id="waveGrad1" x1="0" y1="1" x2="1" y2="0">
                  <stop offset="0%" stopColor="#2dd4bf" stopOpacity="0.12" />
                  <stop offset="50%" stopColor="#60a5fa" stopOpacity="0.32" />
                  <stop offset="100%" stopColor="#c084fc" stopOpacity="0.45" />
                </linearGradient>
                <linearGradient id="waveGrad2" x1="0" y1="1" x2="1" y2="0">
                  <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.08" />
                  <stop offset="55%" stopColor="#38bdf8" stopOpacity="0.22" />
                  <stop offset="100%" stopColor="#a855f7" stopOpacity="0.3" />
                </linearGradient>
              </defs>
            </svg>

            <div className="relative z-10 max-w-[620px]">
              <span className="inline-block text-[11.5px] sm:text-[12px] font-bold uppercase tracking-[0.22em] text-[#86E4D6] mb-3 font-mono">
                FOR PRACTITIONERS
              </span>
              <h2 className="text-white text-2xl sm:text-3xl lg:text-[34px] font-bold leading-[1.22] tracking-[-0.01em] mb-3">
                Teach yoga? Practice Reiki? Counsel on nutrition? Bring your practice to MetaFit.
              </h2>
              <p className="text-white/85 text-[14.5px] sm:text-[15.5px] leading-relaxed">
                Set your own prices, choose your own hours, and get booked by clients across India — online or offline.
              </p>
            </div>
            
            <Link
              to="/trainer-signup"
              className="cta-onboard-btn relative z-10 shrink-0 inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-[14.5px] sm:text-[15px] font-semibold text-[#0F172A] shadow-md transition-all duration-200 hover:scale-[1.03] hover:shadow-lg hover:bg-slate-50"
            >
              Start onboarding →
            </Link>
          </div>
        </div>
      </section>

      {/* FOOTER */}
    </>
  );
};

export default Homepage;
